(function () {
  'use strict';

  function normalizeIds(values) {
    return Array.from(new Set((values || []).filter(Boolean).map(Number).filter(Boolean)));
  }

  function normalizePool(rawPool, defaultThreshold) {
    const variants = normalizeIds(
      Array.isArray(rawPool?.variants)
        ? rawPool.variants
        : [rawPool?.variantId]
    );
    const requestedIndex = Math.max(0, Number(rawPool?.defaultIndex) || 0);
    const defaultIndex = requestedIndex < variants.length ? requestedIndex : 0;
    return Object.freeze({
      variants: Object.freeze(variants),
      variantId: variants[defaultIndex] || variants[0] || 0,
      defaultIndex,
      threshold: Number(rawPool?.threshold) || defaultThreshold,
      useDiscountedTotal: rawPool?.useDiscountedTotal === true,
    });
  }

  function normalizeCodes(values) {
    const list = Array.isArray(values) ? values : String(values || '').split(/[\s,;]+/);
    const seen = new Set();
    const codes = [];
    list.forEach((value) => {
      const code = String(value || '').trim().toUpperCase();
      if (!code || seen.has(code)) return;
      seen.add(code);
      codes.push(code);
    });
    return codes;
  }

  function normalizeCouponPolicy(rawCoupon, fallbackCode) {
    const code = String(rawCoupon?.code || fallbackCode || '').trim().toUpperCase();
    return Object.freeze({
      code,
      autoApply: rawCoupon?.autoApply === true,
      retiredCodes: Object.freeze(normalizeCodes(rawCoupon?.retiredCodes).filter((retired) => retired !== code)),
      exclusiveCodes: Object.freeze(normalizeCodes(rawCoupon?.exclusiveCodes || ['DRNEW'])),
    });
  }

  function normalizeConfig(parsed) {
    if (!parsed) return null;
    const modules = parsed.modules || {};
    const free = normalizeIds(parsed.free);
    const bonus = normalizePool(parsed.bonus, 80000);
    const bonus2 = normalizePool(parsed.bonus2, 120000);
    const bonus3 = normalizePool(parsed.bonus3, 160000);
    const couponCode = String(parsed.couponCode || '').trim().toUpperCase();
    return Object.freeze({
      modules: Object.freeze({
        free: modules.free !== false,
        bonus1: modules.bonus1 !== false,
        bonus2: modules.bonus2 !== false,
        bonus3: modules.bonus3 !== false,
        membersTab: modules.membersTab !== false,
        newMemberTab: modules.newMemberTab !== false,
      }),
      free: Object.freeze(free),
      bonus,
      bonus2,
      bonus3,
      defaultFreeIndex: Number(parsed.defaultFreeIndex) || 0,
      couponCode,
      coupon: normalizeCouponPolicy(parsed.coupon, couponCode),
      sections: Object.freeze(parsed.sections || ['cart-drawer', 'cart-icon-bubble']),
      sectionsUrl: parsed.sectionsUrl || '/cart',
      allGiftVariantIds: Object.freeze(
        normalizeIds(
          Array.isArray(parsed.allGiftVariantIds) && parsed.allGiftVariantIds.length > 0
            ? parsed.allGiftVariantIds
            : [...free, ...bonus.variants, ...bonus2.variants, ...bonus3.variants]
        )
      ),
    });
  }

  function isModuleEnabled(config, key) {
    if (!config || !config.modules || typeof config.modules !== 'object') return true;
    return config.modules[key] !== false;
  }

  function buildAllGiftIdSet(config) {
    return new Set(config?.allGiftVariantIds || []);
  }

  function buildFreeGiftIdSet(config) {
    return new Set(config?.free || []);
  }

  function getPoolSelection(cart, pool, overrideVariantId) {
    const poolSet = new Set(pool?.variants || []);
    const overrideId = Number(overrideVariantId || 0);
    if (overrideId && poolSet.has(overrideId)) return overrideId;
    const selectedLine = (cart?.items || []).find(
      (item) => poolSet.has(Number(item.variant_id)) && Number(item.quantity) > 0
    );
    return selectedLine?.variant_id || pool?.variants?.[pool?.defaultIndex || 0] || pool?.variants?.[0] || null;
  }

  function normalizeSelectionOverrides(value) {
    if (value && typeof value === 'object') return value;
    return { free: Number(value || 0) || null };
  }

  function computeEligibleTotal(cart, config, allGiftSet) {
    const useDiscountedTotal =
      config?.bonus?.useDiscountedTotal === true ||
      config?.bonus2?.useDiscountedTotal === true ||
      config?.bonus3?.useDiscountedTotal === true;
    if (useDiscountedTotal) {
      return Number(cart?.total_price) || 0;
    }
    return (cart?.items || []).reduce((sum, item) => {
      if (allGiftSet.has(item.variant_id)) return sum;
      return sum + (Number(item.original_line_price) || 0);
    }, 0);
  }

  function computeDesiredGiftState(cart, config, freeSet, allGiftSet, selectionOverrides) {
    const freeModuleOn = isModuleEnabled(config, 'free');
    const bonus1ModuleOn = isModuleEnabled(config, 'bonus1');
    const bonus2ModuleOn = isModuleEnabled(config, 'bonus2');
    const bonus3ModuleOn = isModuleEnabled(config, 'bonus3');
    const hasPaid = (cart?.items || []).some((item) => !allGiftSet.has(item.variant_id));

    const overrides = normalizeSelectionOverrides(selectionOverrides);
    if (!hasPaid) {
      return {
        desiredFree: null,
        desiredBonus: null,
        desiredBonus2: null,
        desiredBonus3: null,
        desiredBonusQty: 0,
        desiredBonus2Qty: 0,
        desiredBonus3Qty: 0,
      };
    }

    const freeInCart = (cart?.items || []).filter((item) => freeSet.has(item.variant_id));
    const desiredFree = freeModuleOn
      ? (
        overrides.free && freeSet.has(Number(overrides.free))
          ? Number(overrides.free)
          : freeInCart.length === 1
          ? freeInCart[0].variant_id
          : config.free[config.defaultFreeIndex] || config.free[0] || null
      )
      : null;
    const eligibleTotal = computeEligibleTotal(cart, config, allGiftSet);
    const desiredBonus = bonus1ModuleOn && config.bonus.variants.length && eligibleTotal >= config.bonus.threshold
      ? getPoolSelection(cart, config.bonus, overrides.bonus)
      : null;
    const desiredBonus2 = bonus2ModuleOn && config.bonus2.variants.length && eligibleTotal >= config.bonus2.threshold
      ? getPoolSelection(cart, config.bonus2, overrides.bonus2)
      : null;
    const desiredBonus3 = bonus3ModuleOn && config.bonus3.variants.length && eligibleTotal >= config.bonus3.threshold
      ? getPoolSelection(cart, config.bonus3, overrides.bonus3)
      : null;

    return {
      desiredFree,
      desiredBonus,
      desiredBonus2,
      desiredBonus3,
      desiredBonusQty: desiredBonus ? 1 : 0,
      desiredBonus2Qty: desiredBonus2 ? 1 : 0,
      desiredBonus3Qty: desiredBonus3 ? 1 : 0,
    };
  }

  function buildSyntheticEmptyCart() {
    return {
      items: [],
      total_price: 0,
      item_count: 0,
    };
  }

  function planAtomicAddGiftLines(opts = {}) {
    const config = opts.config;
    if (!config || config.free.length === 0 || !isModuleEnabled(config, 'free')) {
      return { giftLines: [], reason: 'no_config' };
    }

    const paidVariantId = Number(opts.variantId || 0);
    const paidQuantity = Math.max(1, Number(opts.quantity) || 1);
    if (!paidVariantId) {
      return { giftLines: [], reason: 'invalid_variant' };
    }

    const freeSet = buildFreeGiftIdSet(config);
    const allGiftSet = buildAllGiftIdSet(config);
    if (allGiftSet.has(paidVariantId)) {
      return { giftLines: [], reason: 'paid_variant_is_gift' };
    }

    let baseCart = null;
    if (Array.isArray(opts.cart?.items) && typeof opts.cart?.item_count === 'number') {
      baseCart = opts.cart;
    } else if (opts.assumeEmpty === true) {
      baseCart = buildSyntheticEmptyCart();
    } else {
      return { giftLines: [], reason: 'missing_snapshot' };
    }

    const qtyByVariant = new Map();
    let hasPaidAfterAdd = false;
    let existingSelectedFree = null;
    let freeLineCount = 0;

    (baseCart.items || []).forEach((item) => {
      const variantId = Number(item.variant_id || 0);
      const qty = Math.max(0, Number(item.quantity) || 0);
      if (!variantId || qty <= 0) return;
      qtyByVariant.set(variantId, (qtyByVariant.get(variantId) || 0) + qty);
      if (freeSet.has(variantId)) {
        freeLineCount += 1;
        existingSelectedFree = variantId;
      }
      if (!allGiftSet.has(variantId)) hasPaidAfterAdd = true;
    });

    qtyByVariant.set(paidVariantId, (qtyByVariant.get(paidVariantId) || 0) + paidQuantity);
    hasPaidAfterAdd = true;

    if (!hasPaidAfterAdd) {
      return { giftLines: [], reason: 'no_paid_after_add' };
    }

    const hasAnyFreeAlready = config.free.some((variantId) => (qtyByVariant.get(variantId) || 0) > 0);
    if (hasAnyFreeAlready) {
      return { giftLines: [], reason: 'free_already_present' };
    }

    const desiredFree =
      (freeLineCount === 1 && existingSelectedFree && freeSet.has(existingSelectedFree) && existingSelectedFree) ||
      config.free[config.defaultFreeIndex] ||
      config.free[0] ||
      null;

    if (!desiredFree) {
      return { giftLines: [], reason: 'no_desired_free' };
    }

    return {
      giftLines: [{ id: desiredFree, quantity: 1 }],
      reason: 'planned_default_free',
    };
  }

  function buildGiftUpdatePlan(opts = {}) {
    const config = opts.config;
    const cart = opts.cart;
    const cleanupCandidateIds = Array.isArray(opts.cleanupCandidateIds)
      ? opts.cleanupCandidateIds
      : Array.from(opts.cleanupCandidateIds || []);
    const freeSet = buildFreeGiftIdSet(config);
    const allGiftSet = buildAllGiftIdSet(config);
    const desiredState = computeDesiredGiftState(
      cart,
      config,
      freeSet,
      allGiftSet,
      opts.desiredGiftSelections || opts.desiredFreeGift
    );
    const updates = {};
    let needsUpdate = false;

    cleanupCandidateIds.forEach((variantId) => {
      const numericVariantId = Number(variantId || 0);
      if (!numericVariantId) return;
      const currentQty = (cart?.items || []).find((item) => item.variant_id === numericVariantId)?.quantity || 0;
      let desiredQty = 0;
      if (numericVariantId === desiredState.desiredFree) {
        desiredQty = 1;
      } else if (numericVariantId === desiredState.desiredBonus) {
        desiredQty = 1;
      } else if (numericVariantId === desiredState.desiredBonus2) {
        desiredQty = 1;
      } else if (numericVariantId === desiredState.desiredBonus3) {
        desiredQty = 1;
      }
      if (currentQty !== desiredQty) {
        updates[numericVariantId] = desiredQty;
        needsUpdate = true;
      }
    });

    return {
      desiredState,
      updates,
      needsUpdate,
      freeSet,
      allGiftSet,
    };
  }

  function validateFreeGiftState(cart, config, allGiftSet, freeSet) {
    const freeModuleOn = isModuleEnabled(config, 'free');
    if (!freeModuleOn) {
      const eligibleTotal = computeEligibleTotal(cart, config, allGiftSet);
      const allowedBonusIds = new Set();
      if (
        isModuleEnabled(config, 'bonus1') &&
        config.bonus?.variants?.length &&
        eligibleTotal >= (config.bonus?.threshold || 0)
      ) {
        config.bonus.variants.forEach((variantId) => allowedBonusIds.add(variantId));
      }
      if (
        isModuleEnabled(config, 'bonus2') &&
        config.bonus2?.variants?.length &&
        eligibleTotal >= (config.bonus2?.threshold || 0)
      ) {
        config.bonus2.variants.forEach((variantId) => allowedBonusIds.add(variantId));
      }
      if (
        isModuleEnabled(config, 'bonus3') &&
        config.bonus3?.variants?.length &&
        eligibleTotal >= (config.bonus3?.threshold || 0)
      ) {
        config.bonus3.variants.forEach((variantId) => allowedBonusIds.add(variantId));
      }
      const invalidFreeLines = (cart?.items || []).filter(
        (item) => freeSet.has(item.variant_id) && !allowedBonusIds.has(item.variant_id)
      );
      return invalidFreeLines.length === 0;
    }
    const hasPaid = (cart?.items || []).some((item) => !allGiftSet.has(item.variant_id));
    const freeInCart = (cart?.items || []).filter((item) => freeSet.has(item.variant_id));
    if (hasPaid) return freeInCart.length === 1 && freeInCart[0].quantity === 1;
    return freeInCart.length === 0;
  }

  function validateBonusGiftState(cart, config, allGiftSet) {
    const eligibleTotal = computeEligibleTotal(cart, config, allGiftSet);
    const bonusChecks = [
      { pool: config.bonus, moduleOn: isModuleEnabled(config, 'bonus1') },
      { pool: config.bonus2, moduleOn: isModuleEnabled(config, 'bonus2') },
      { pool: config.bonus3, moduleOn: isModuleEnabled(config, 'bonus3') },
    ].filter((check) => check.pool?.variants?.length);

    for (const check of bonusChecks) {
      const poolSet = new Set(check.pool.variants);
      const lines = (cart?.items || []).filter((item) => poolSet.has(item.variant_id) && item.quantity > 0);
      const shouldHaveGift = check.moduleOn && eligibleTotal >= check.pool.threshold;
      if (shouldHaveGift) {
        if (lines.length !== 1 || lines[0].quantity !== 1) return false;
      } else if (lines.length > 0) {
        return false;
      }
    }

    return true;
  }

  function validateCheckoutCart(cart, config) {
    const freeSet = buildFreeGiftIdSet(config);
    const allGiftSet = buildAllGiftIdSet(config);
    if (!Array.isArray(cart?.items) || cart.items.length === 0) {
      return { ok: false, reason: 'empty_cart' };
    }
    const hasPaid = cart.items.some((item) => !allGiftSet.has(item.variant_id));
    if (!hasPaid && cart.items.length > 0) {
      return { ok: false, reason: 'gift_only_cart' };
    }
    if (!validateFreeGiftState(cart, config, allGiftSet, freeSet)) {
      return { ok: false, reason: 'free_gift_invalid' };
    }
    if (!validateBonusGiftState(cart, config, allGiftSet)) {
      return { ok: false, reason: 'bonus_gift_invalid' };
    }
    return { ok: true, reason: 'valid' };
  }

  // Every code attached to the cart. `discount_codes` also lists codes Shopify
  // no longer applies (expired, below minimum), which the applications miss.
  function extractCartDiscountCodes(cart) {
    const found = [];
    (cart?.discount_codes || []).forEach((entry) => {
      found.push(typeof entry === 'string' ? entry : entry?.code);
    });
    const cartLevel = cart?.cart_level_discount_applications || cart?.discount_applications || [];
    cartLevel.forEach((discount) => {
      if (discount?.type && discount.type !== 'discount_code') return;
      found.push(discount?.code || discount?.title);
    });
    (cart?.items || []).forEach((item) => {
      (item.line_level_discount_allocations || []).forEach((allocation) => {
        const app = allocation?.discount_application || allocation;
        if (app?.type && app.type !== 'discount_code') return;
        found.push(app?.code || app?.title);
      });
    });
    return normalizeCodes(found);
  }

  function sameCodeSet(a, b) {
    if (a.length !== b.length) return false;
    const set = new Set(a);
    return b.every((code) => set.has(code));
  }

  /**
   * Plans the cart's discount list: drop retired sitewide codes, keep every
   * other code (club coupons, DRNEW), and add the current sitewide code when
   * auto-apply is on, when a retired code was swapped out, or when the shopper
   * asked for it. One write, only when the set of codes changes.
   */
  function planCouponCodes(opts = {}) {
    const cart = opts.cart;
    const config = opts.config;
    const policy = config?.coupon || normalizeCouponPolicy(null, config?.couponCode);
    const mainCode = policy.code;
    const retired = new Set(policy.retiredCodes);
    const exclusive = new Set(policy.exclusiveCodes);
    const requested = normalizeCodes(opts.requestedCodes || []);
    const current = extractCartDiscountCodes(cart);
    const candidates = normalizeCodes([...current, ...requested]);
    const removed = candidates.filter((code) => retired.has(code));
    const kept = candidates.filter((code) => !retired.has(code));
    const allGiftSet = buildAllGiftIdSet(config);
    const hasPaid = (cart?.items || []).some((item) => !allGiftSet.has(Number(item.variant_id)));
    const noChange = { codes: current, needsUpdate: false, removed: [], added: false };

    if (!hasPaid && requested.length === 0) return noChange;

    const mainRequested = !!mainCode && requested.includes(mainCode);
    const blockedByExclusive = kept.some((code) => exclusive.has(code));
    const wantsMain =
      !!mainCode &&
      hasPaid &&
      (mainRequested ||
        (!opts.suppressMain && !blockedByExclusive && (policy.autoApply || removed.length > 0)));
    const codes = wantsMain && !kept.includes(mainCode) ? [...kept, mainCode] : kept;
    if (sameCodeSet(codes, current)) return noChange;
    return {
      codes,
      needsUpdate: true,
      removed,
      added: codes.includes(mainCode) && !current.includes(mainCode),
    };
  }

  window.drGiftRulesEngine = {
    normalizeConfig,
    extractCartDiscountCodes,
    planCouponCodes,
    isModuleEnabled,
    buildAllGiftIdSet,
    buildFreeGiftIdSet,
    computeEligibleTotal,
    computeDesiredGiftState,
    buildSyntheticEmptyCart,
    planAtomicAddGiftLines,
    buildGiftUpdatePlan,
    validateFreeGiftState,
    validateBonusGiftState,
    validateCheckoutCart,
  };
})();
